import { gql, useMutation } from '@apollo/client';
import { convertFromRaw } from 'draft-js';
import * as React from 'react';

import { analyzeContent, post_created } from '@/analytics';
import { CreateBlogDataInput_V2 } from '@/graphql-types/globalTypes';
import { logError } from '@/imports/logging/ClientLogger';

import {
  useCreateBlog_createBlogV2,
  useCreateBlog_createBlogV2Variables,
} from './graphql-types/useCreateBlog_createBlogV2';

const createBlogWithPostAST = gql`
  mutation useCreateBlog_createBlogV2($input: CreateBlogInput_V2!) {
    createBlogV2(input: $input) {
      data {
        author {
          _id
          userOnboardingChecklistState {
            userHasPosted
          }
        }
        _id
        group {
          _id
          isGlobalSingleton
        }
      }
    }
  }
`;

type BlogMutationOptions = {
  publish: boolean;
  isAnnouncement: boolean;
  alreadyPublished: boolean;
  scheduledPublishDate: Date | null;
};

type CreateBlogOptions = {
  setPostId: React.Dispatch<React.SetStateAction<string | undefined>>;
  blog: Omit<CreateBlogDataInput_V2, 'published'> & {
    recommendedHashtags?: string[];
  };
  options: BlogMutationOptions;
};

export const useCreateBlog = (): {
  createBlog: (options: CreateBlogOptions) => Promise<boolean>;
} => {
  const [createFunc] = useMutation<
    useCreateBlog_createBlogV2,
    useCreateBlog_createBlogV2Variables
  >(createBlogWithPostAST, {
    refetchQueries: ['DraftsPage_GetPosts'],
  });

  const createBlog = React.useCallback(
    async (options: CreateBlogOptions) => {
      const {
        isAnnouncement,
        alreadyPublished,
        publish,
        scheduledPublishDate,
      } = options.options;
      const { blog } = options;

      try {
        const { recommendedHashtags, ...blogData } = blog;
        const result = await createFunc({
          variables: {
            input: {
              data: {
                ...blogData,
                published: publish,
                scheduledPublishDate:
                  scheduledPublishDate?.toISOString() ?? null,
                ...(recommendedHashtags?.length
                  ? { recommendedHashtags }
                  : {}),
              },
              isAnnouncement: isAnnouncement,
            },
          },
          update(cache: any) {
            cache.evict({
              id: 'ROOT_QUERY',
              fieldName: 'activityByAuthorId',
            });
            cache.gc();
          },
        });

        const resultBlogPost = result.data?.createBlogV2?.data;
        if (resultBlogPost?.__typename !== 'BlogV2') {
          throw new Error('no blogv2 returned from createBlogWithPostV2');
        }

        options.setPostId(resultBlogPost?._id);

        if (!alreadyPublished && publish) {
          const postedIn = !!resultBlogPost.group?.isGlobalSingleton
            ? 'home'
            : !!blog.organizationId
            ? 'organization'
            : 'group';

          const bodyText = `${blog.title}\n${convertFromRaw(
            blog.content
          ).getPlainText()}`;
          const contentAnalysis = analyzeContent(bodyText);

          post_created({
            entity_type: 'post',
            entity_id: resultBlogPost._id,
            post_type: 'article',
            content_type: 'article',
            post_is_announcement: !!isAnnouncement,
            post_is_scheduled: !!scheduledPublishDate,
            location_posted_in: postedIn,
            location_group_id:
              !blog?.organizationId && blog.groupId ? blog.groupId : undefined,
            location_organization_id: blog?.organizationId || undefined,
            content_text: contentAnalysis.contentText,
            content_length: contentAnalysis.contentLength,
            content_word_count: contentAnalysis.wordCount,
            content_has_question_mark: contentAnalysis.hasQuestionMark,
            content_has_links: contentAnalysis.hasLinks,
            content_link_count: contentAnalysis.linkCount,
            content_mention_count: contentAnalysis.mentionCount,
            content_hashtag_count: contentAnalysis.hashtagCount,
            content_hashtags: contentAnalysis.hashtags,
            content_has_tags: !!blog.tagIds && blog.tagIds.length > 0,
            content_has_media: !!blog.blogBannerImageId,
          });
        }
        return true;
      } catch (err) {
        logError(err);
        return false;
      }
    },
    [createFunc]
  );

  return {
    createBlog,
  };
};
